/**
 * 重连退避与节流。
 *
 * 约束来源：需求文档 9.4 "重连采用指数退避与随机抖动"，
 * 避免服务恢复时集中重连再次打垮依赖。SDK 侧同时提供"最新值优先"的合并节流，
 * 保证 iframe 未就绪时只缓存最新必要状态，不无限排队。
 */

export interface BackoffOptions {
  /** 随机抖动比例，0~1，默认 0.2。 */
  jitterRatio?: number;
  /** 最大等待毫秒，默认 30000。 */
  maxDelayMs?: number;
  /** 首次等待毫秒，默认 500。 */
  minDelayMs?: number;
}

export class Backoff {
  private attempt = 0;

  private readonly jitterRatio: number;

  private readonly maxDelayMs: number;

  private readonly minDelayMs: number;

  constructor(options: BackoffOptions = {}) {
    this.minDelayMs = Math.max(1, options.minDelayMs ?? 500);
    this.maxDelayMs = Math.max(this.minDelayMs, options.maxDelayMs ?? 30_000);
    this.jitterRatio = Math.min(1, Math.max(0, options.jitterRatio ?? 0.2));
  }

  /** 下一次等待毫秒数（含抖动），并把尝试次数加一。 */
  next(): number {
    const exp = Math.min(this.maxDelayMs, this.minDelayMs * 2 ** this.attempt);
    this.attempt += 1;
    const jitter = exp * this.jitterRatio * (Math.random() * 2 - 1);
    return Math.max(0, Math.round(exp + jitter));
  }

  reset(): void {
    this.attempt = 0;
  }

  get attempts(): number {
    return this.attempt;
  }
}

/**
 * 合并节流器：同一 key 只保留"最新一次"调用，用于
 * - 页面上下文上报去重节流；
 * - iframe 未就绪时缓存最新必要状态（容量上界为 1 条/键，绝不无限排队）。
 */
export class LatestOnlyQueue<K, V> {
  private readonly items = new Map<K, V>();

  enqueue(key: K, value: V): void {
    this.items.set(key, value);
  }

  get size(): number {
    return this.items.size;
  }

  drain(): Array<[K, V]> {
    const entries = [...this.items.entries()];
    this.items.clear();
    return entries;
  }

  peek(key: K): undefined | V {
    return this.items.get(key);
  }

  clear(): void {
    this.items.clear();
  }
}